import { defineComponent as _defineComponent } from 'vue'
import ContentSSR from '@/components/ContentSSR.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import NavigationTab from '@/components/nav/NavigationTab.vue';
import MyLists from '@/components/watchlist/my-lists/MyLists.vue';
import PublicLists from '@/components/watchlist/public-lists/PublicLists.vue';
import TVShowTracking from '@/components/watchlist/tv-show-tracking/TVShowTracking.vue';
import { CollectionType } from '@/enums/collection-type';
import { useI18n } from '@/helpers/composables/useI18n';
import { useConstantStore, useFilterStore, useRoutingStore } from '@/helpers/composables/useStores';
import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { FilterCollection } from '@/stores/filter.store';
import { ListsCollectionType } from '@/stores/routing.store';
import { computed, ref } from 'vue';
import { RouterLink } from 'vue-router';
import { useUserRentals } from '@/features/jwtv-titles-rental/composables/useUserRentals';

type ListsTab = {
	key: 'my_lists' | 'public_lists' | 'tv_show_tracking' | 'my_rentals';
	translation: string;
	collectionType: ListsCollectionType;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'Watchlist',
  setup(__props) {

const UserRentals = () => import('@/pages/UserRentals.vue');

const { tc } = useI18n();
const { filters } = useFilterStore();
const { genresByShortName, providersByShortName, subgenresList } = useConstantStore();
const { activeRoute, queryParams } = useRoutingStore();
const { allUserRentals } = useUserRentals();

const content = ref<{ scrollToTop: (duration: number, behavior: 'smooth' | 'auto') => void } | null>();

const pageType = computed(() => activeRoute.value.path.split('/').pop()?.replaceAll('-', '_') || 'public_lists');

const listComponent = computed(() => {
	if (pageType.value === 'my_lists') return MyLists;
	if (pageType.value === 'tv_show_tracking') return TVShowTracking;
	if (pageType.value === 'my_rentals') return UserRentals;

	// safe default to PublicLists since `pageType` is using the same fallback
	return PublicLists;
});

const watchlistTabs = computed<ListsTab[]>(() => [
	{
		key: 'my_lists',
		translation: tc('WEBAPP_MYLISTS'),
		collectionType: CollectionType.MY_LISTS,
	},
	...((allUserRentals.value.length > 0
		? [
				{
					key: 'my_rentals',
					translation: 'My Rentals',
					collectionType: CollectionType.MY_RENTALS,
				},
		  ]
		: []) as ListsTab[]),
	{
		key: 'public_lists',
		translation: tc('WEBAPP_LISTS_PUBLIC_LISTS'),
		collectionType: CollectionType.PUBLIC_LISTS,
	},
	{
		key: 'tv_show_tracking',
		translation: tc('WEBAPP_LISTS_TV_SHOW_TRACKING'),
		collectionType: CollectionType.TV_SHOW_TRACKING,
	},
]);

function tabRouteParams(tab: ListsTab) {
	const params = routeParams(tab.collectionType);
	params.query.list_id = undefined;

	return params;
}

const newPageCollectionType = [CollectionType.COMING_SOON, CollectionType.LEAVING_SOON, CollectionType.PRICEDROPS];

/**
 * with these parameters we can always link to the correct url for each collectionType page.
 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
 */
function routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
	const filterCollectionType = newPageCollectionType.includes(collectionType) ? CollectionType.NEW : collectionType;
	const filterValues = filterOverwrite ?? filters.value(filterCollectionType);
	const routeName = getRouteName(collectionType, filterValues);
	const params = getUrlParamsFromFilterState(
		routeName,
		filterValues,
		providersByShortName.value,
		genresByShortName.value,
		collectionType,
		queryParams.value,
		subgenresList.value
	);

	return { name: routeName, ...params };
}

function scrollToTop() {
	content.value?.scrollToTop(0, 'auto');
}

function onTabClick(tab: ListsTab) {
	scrollToTop();

	TrackingHelper.trackEvent('userinteraction', {
		action: 'filter_toggled',
		label: 'list_types',
		property: tab.key,
	});
}

return { __sfc: true,UserRentals, tc, filters, genresByShortName, providersByShortName, subgenresList, activeRoute, queryParams, allUserRentals, content, pageType, listComponent, watchlistTabs, tabRouteParams, newPageCollectionType, routeParams, scrollToTop, onTabClick, ContentSSR, HiddenHorizontalScrollbar, NavigationTab, RouterLink }
}

})